// @oasis-permission-inherits: /videos (mirage-videos:view)
import { ApiUnavailableError, KnownApiError } from "@mirage-web/networking/known-api-error";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { ReferenceThumbnail } from "~/modules/segment-review/components/reference-thumbnail";
import { submitSegmentReviewMutation } from "~/modules/segment-review/mutations";
import { fetchSegmentReviewFeedQuery } from "~/modules/segment-review/queries";
import { FeedSegment, SegmentReviewVerdict } from "~/modules/segment-review/schemas";
import { VideoCard } from "~/modules/videos/components/video-card";

import { SegmentEvalNav } from "./-components/segment-eval-nav";

export const Route = createFileRoute("/_dash/videos/segment-eval/")({
  component: RouteComponent,
});

function isEndpointMissing(error: Error | null): boolean {
  return (
    (error instanceof KnownApiError && error.responseStatus === 404) ||
    (error instanceof ApiUnavailableError && error.responseStatus === 404)
  );
}

const SPEED_OPTIONS = [
  { rate: 1, label: "1×" },
  { rate: 0.5, label: "0.5×" },
];

function SegmentFeedCard({
  segment,
  verdict,
  onVerdict,
}: {
  segment: FeedSegment;
  verdict: SegmentReviewVerdict | undefined;
  onVerdict: (segment: FeedSegment, verdict: SegmentReviewVerdict) => void;
}) {
  const [playbackRate, setPlaybackRate] = useState(1);
  const [aspectRatio, setAspectRatio] = useState(9 / 16);

  return (
    <div
      className="relative"
      style={{
        aspectRatio: String(aspectRatio),
        width: `min(100%, calc((100cqh - 3rem) * ${aspectRatio}))`,
      }}
    >
      <VideoCard
        video={{
          id: segment.videoId,
          createdAt: segment.videoCreatedAt ?? segment.videoCompletedAt ?? 0,
          completedAt: segment.videoCompletedAt,
          initialVideoUrl: segment.url,
          caller: "",
        }}
        showBookmarkButton={false}
        onEnded={() => onVerdict(segment, "watched")}
        playbackRate={playbackRate}
        autoPlayWhenVisible
        loopPlayback
        adaptToVideoAspect
        onAspectRatioChange={setAspectRatio}
      />
      <ReferenceThumbnail
        url={segment.referenceUrl}
        type={segment.referenceType}
        promptText={segment.promptText}
      />
      <div className="absolute top-1/2 right-2 z-10 flex -translate-y-1/2 flex-col items-end gap-1.5">
        <Button
          size="icon"
          variant="ghost"
          className={`h-14 w-14 rounded-full text-4xl ${
            verdict === "rejected"
              ? "bg-red-600/80 ring-2 ring-red-400 hover:bg-red-600/80"
              : "bg-black/50 hover:bg-black/70"
          }`}
          aria-label="Horrified"
          aria-pressed={verdict === "rejected"}
          onClick={() => onVerdict(segment, "rejected")}
        >
          <span>😱</span>
        </Button>
        <div
          className="flex overflow-hidden rounded-full bg-black/50"
          role="group"
          aria-label="Playback speed"
        >
          {SPEED_OPTIONS.map((option) => (
            <button
              key={option.rate}
              type="button"
              className={`px-2.5 py-1 text-xs transition-colors ${
                playbackRate === option.rate
                  ? "bg-white/90 font-semibold text-black"
                  : "text-white hover:bg-black/70"
              }`}
              aria-pressed={playbackRate === option.rate}
              onClick={() => setPlaybackRate(option.rate)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <Button
          asChild
          size="icon"
          variant="ghost"
          className="h-8 w-8 rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white"
        >
          <Link
            to="/videos/view/$videoId"
            params={{ videoId: segment.videoId }}
            aria-label="Open video details"
          >
            <ExternalLink className="size-4" />
          </Link>
        </Button>
        {verdict === "watched" && (
          <span className="pointer-events-none rounded bg-background/80 px-1.5 py-0.5 text-xs text-foreground">
            Watched ✓
          </span>
        )}
      </div>
    </div>
  );
}

function RouteComponent() {
  const feedRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const inFlightRef = useRef(new Set<string>());

  const { data, error, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    fetchSegmentReviewFeedQuery.useInfiniteQuery(
      { params: {} },
      { retry: false, staleTime: Infinity, refetchOnWindowFocus: false }
    );

  const [verdicts, setVerdicts] = useState<Record<string, SegmentReviewVerdict>>({});

  const seenSegmentIds = new Set<string>();
  const segments = (data?.pages.flatMap((page) => page.segments.page) ?? []).filter((segment) => {
    if (seenSegmentIds.has(segment.segmentId)) {
      return false;
    }
    seenSegmentIds.add(segment.segmentId);
    return true;
  });

  const frontierIndex = segments.findIndex((segment) => !verdicts[segment.segmentId]);
  const allReviewed = frontierIndex === -1;
  const visibleSegments = allReviewed ? segments : segments.slice(0, frontierIndex + 1);
  const unreviewedBuffered = allReviewed ? 0 : segments.length - frontierIndex;

  const submitReview = submitSegmentReviewMutation.useMutation({
    onSuccess: (result) => {
      setVerdicts((prev) => ({ ...prev, [result.review.segmentId]: result.review.verdict }));
    },
    onError: () => {
      toast.error("Failed to submit review");
    },
  });

  const submitVerdict = (segment: FeedSegment, verdict: SegmentReviewVerdict) => {
    const current: SegmentReviewVerdict | undefined = verdicts[segment.segmentId];
    if (current === "rejected" || inFlightRef.current.has(segment.segmentId)) {
      return;
    }
    if (verdict === "watched" && current === "watched") {
      return;
    }
    inFlightRef.current.add(segment.segmentId);
    submitReview.mutate(
      {
        pathParams: { segmentId: segment.segmentId },
        params: { verdict },
      },
      {
        onSettled: () => {
          inFlightRef.current.delete(segment.segmentId);
        },
      }
    );
  };

  const endpointMissing = isEndpointMissing(error);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage || unreviewedBuffered >= 6) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { root: feedRef.current, rootMargin: "200% 0px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, segments.length, unreviewedBuffered]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">yuck that's cooked ™</h1>
        <SegmentEvalNav active="feed" />
      </div>

      {endpointMissing && (
        <div className="rounded-md border border-dashed p-5 text-sm text-muted-foreground">
          The segment review feed is not available yet (the captions-server endpoint is pending
          deploy).
        </div>
      )}
      {error && !endpointMissing && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm">
          <p className="font-medium">Failed to load the feed</p>
          <pre className="mt-2 overflow-x-auto text-xs">{error.message}</pre>
        </div>
      )}

      <div
        ref={feedRef}
        className="min-h-0 flex-1 snap-y snap-mandatory overflow-y-auto overscroll-contain [container-type:size]"
      >
        {visibleSegments.map((segment) => (
          <div
            key={segment.segmentId}
            className="flex h-full snap-start flex-col items-center justify-center gap-2 py-1"
          >
            <SegmentFeedCard
              segment={segment}
              verdict={verdicts[segment.segmentId]}
              onVerdict={submitVerdict}
            />
            <span
              className={`text-xs text-muted-foreground ${
                verdicts[segment.segmentId] ? "invisible" : ""
              }`}
            >
              Watch to the end (or react 😱) to unlock the next segment
            </span>
          </div>
        ))}
        {allReviewed && (isLoading || isFetchingNextPage) && !endpointMissing && (
          <div className="flex h-full snap-start items-center justify-center py-1">
            <Skeleton className="aspect-9/16 h-full" />
          </div>
        )}
        {!isLoading && !error && segments.length === 0 && !hasNextPage && (
          <div className="flex h-full snap-start flex-col items-center justify-center gap-2 text-muted-foreground">
            <span className="text-3xl">🎉</span>
            <span className="text-sm">All caught up — every segment has enough reviews</span>
          </div>
        )}
        {!isLoading &&
          segments.length > 0 &&
          allReviewed &&
          !hasNextPage &&
          !isFetchingNextPage && (
            <div className="flex h-full snap-start flex-col items-center justify-center gap-2 text-muted-foreground">
              <span className="text-3xl">🎉</span>
              <span className="text-sm">You're all caught up</span>
            </div>
          )}
        <div ref={sentinelRef} className="h-px" />
      </div>
    </div>
  );
}
